/**
 * Editor settings — domain types + their default values.
 *
 * Extracted from editorStore.ts so the settings shapes (gameplay, particles,
 * mannequin, obstacles, automapper, interface) and their defaults live on
 * their own, away from the store wiring. Re-exported by editorStore for
 * backward-compatible imports. Depends only on the shared theme tokens.
 */

import { color } from '../components/ui/theme';
import { desktopWallDefaults, DESKTOP_MODEL_SCALE } from '../components/three/wallGeometry';

export type GridGuideType = 'solid' | 'outline' | 'circles';

// ── App-level routing ───────────────────────────────────────────────────────
// Tiny client-side router. The Synapse suite ships multiple editors and a
// landing dashboard; we keep the active one in store state so we don't need
// react-router for two screens.
export type ActiveView = 'dashboard' | 'synth-rider' | 'flow' | 'console';

// ── Figure mode ─────────────────────────────────────────────────────────────

export type FigureMode = 'off' | 'mannequin' | 'mesh';

// ── Render mode ──────────────────────────────────────────────────────────────

export type RenderMode = 'auto' | 'gpu' | 'cpu';

// ── Settings Types & Defaults ────────────────────────────────────────────────

export interface MannequinSettings {
  headRadius: number;
  shoulderWidth: number;
  upperArmLength: number;
  lowerArmLength: number;
  handRadius: number;
  torsoRadius: number;
  figureHeight: number;       // multiplier: 1.0 = default, scales all Y positions
  anticipationBeats: number;
  lerpSpeed: number;
}

// ── Per-obstacle customization ──────────────────────────────────────────────

export type ObstacleShape = 'regular' | 'diagonal' | 'center' | 'square' | 'triangle';

export interface ObstacleConfig {
  shape: ObstacleShape;
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  defaultX: number;       // default X position offset
  defaultY: number;       // default Y position offset
  rotation: number;       // Z-axis rotation in degrees
}

/** The wall_type keys that have per-obstacle settings */
export const OBSTACLE_CONFIG_KEYS = [
  'wall_left', 'wall_right', 'angle_left', 'angle_right', 'center', 'crouch',
] as const;
export type ObstacleConfigKey = typeof OBSTACLE_CONFIG_KEYS[number];

export const OBSTACLE_SHAPE_FBX: Record<ObstacleShape, { fbx: string; texture: string }> = {
  regular:  { fbx: '/models/obstacles/RegularObstacle.fbx', texture: '/models/obstacles/Mat_Color_RegularObstacle_Outlines.png' },
  diagonal: { fbx: '/models/obstacles/DiagonalObstacle.fbx', texture: '/models/obstacles/Mat_Color_DiagonalObstacle_Outlines.png' },
  center:   { fbx: '/models/obstacles/CenterObstacle.fbx', texture: '/models/obstacles/Mat_Color_CenterObstacle_Outlines.png' },
  square:   { fbx: '/models/obstacles/SquareObstacle2.fbx', texture: '/models/obstacles/SquareObstacleTexture.png' },
  triangle: { fbx: '/models/obstacles/TriangleObstacle2.fbx', texture: '/models/obstacles/TriangleObstacleTexture.png' },
};

// Pos X / Pos Y, Scale X / Y and Rotation reproduce the desktop editor's wall prefabs — see
// components/three/wallGeometry. Pos X / Pos Y are an offset ADDED to the wall's file position, never a
// replacement for it ([SRVR-0064]); Scale Z is the web editor's own (its depth axis is time).
export const DEFAULT_OBSTACLE_CONFIGS: Record<ObstacleConfigKey, ObstacleConfig> = {
  wall_left:    { shape: 'regular',  scaleZ: 1, ...desktopWallDefaults('wall_left') },
  wall_right:   { shape: 'regular',  scaleZ: 1, ...desktopWallDefaults('wall_right') },
  angle_left:   { shape: 'diagonal', scaleZ: 1, ...desktopWallDefaults('angle_left') },
  angle_right:  { shape: 'diagonal', scaleZ: 1, ...desktopWallDefaults('angle_right') },
  center:       { shape: 'center',   scaleZ: 1, ...desktopWallDefaults('center') },
  crouch:       { shape: 'regular',  scaleZ: 1, ...desktopWallDefaults('crouch') },
};

export interface GameplaySettings {
  noteSize: number;
  railWidth: number;
  railNodeSize: number;       // Size of rail control-point spheres (smaller than noteSize)
  // Note colors (used by notes, rails, mannequin hands, UI buttons, minimap)
  noteColorLeft: string;
  noteColorRight: string;
  noteColorOneHand: string;
  noteColorBothHands: string;
  // Note material properties
  noteMetalness: number;
  noteRoughness: number;
  noteFresnelEnabled: boolean;
  noteFresnelColor: string;
  noteFresnelIntensity: number;
  noteFresnelPower: number;
  obstacleScale: number;      // global base scale
  // Global obstacle color/Fresnel settings (shared by all obstacle types)
  obstacleFillColor: string;
  obstacleFillOpacity: number;
  obstacleFresnelColor: string;
  obstacleFresnelOpacity: number;
  obstacleFresnelIntensity: number;
  // Per-face outline texture (the game's *_Outlines atlas) fed as the emissiveMap.
  obstacleOutlineColor: string;    // colour the outline edges glow in
  obstacleOutlineEmission: number; // outline glow strength (emissiveIntensity)
  obstacleConfigs: Record<ObstacleConfigKey, ObstacleConfig>;
  // ── Sound effects — synthesised voices shared with the Flow editor
  //    (place note / delete note / mouse-wheel scroll tick). ────────────────
  scrollSound: SrScrollSound;
  scrollVolume: number;   // 0..1
  placeSound: SrPlaceSound;
  placeVolume: number;    // 0..1
  deleteSound: SrDeleteSound;
  deleteVolume: number;   // 0..1
  /** Voice for the NOTE HIT itself (tick vs the sampled desktop-editor clap). */
  noteSound: SrNoteSound;
}

// SFX voice options — mirror the Flow sound module's exported unions so the
// same synthesised voices are available in every editor.
export type SrScrollSound = 'classic' | 'click' | 'soft';
export type SrPlaceSound  = 'pop' | 'blip' | 'thock' | 'quack' | 'none';
export type SrDeleteSound = 'shatter' | 'zap' | 'thud' | 'none';
export type SrNoteSound   = 'tone' | 'clap';

export const DEFAULT_MANNEQUIN: MannequinSettings = {
  headRadius: 0.12, shoulderWidth: 0.25,
  upperArmLength: 0.40, lowerArmLength: 0.40,
  handRadius: 0.08, torsoRadius: 0.035,
  figureHeight: 1.0, anticipationBeats: 0.5, lerpSpeed: 8.0,
};

export const DEFAULT_GAMEPLAY: GameplaySettings = {
  noteSize: 0.175, railWidth: 9, railNodeSize: 0.10,
  noteColorLeft: '#4488FF',
  noteColorRight: '#FF69B4',
  noteColorOneHand: '#44DD66',
  noteColorBothHands: '#FFD700',
  noteMetalness: 0.4,
  noteRoughness: 0.2,
  noteFresnelEnabled: false,
  noteFresnelColor: '#ffffff',
  noteFresnelIntensity: 1.0,
  noteFresnelPower: 2.0,
  obstacleScale: DESKTOP_MODEL_SCALE, // the desktop import scale of the centimetre-unit models
  obstacleFillColor: '#4444ff',
  obstacleFillOpacity: 0.15,
  obstacleFresnelColor: '#4444ff',
  obstacleFresnelOpacity: 0.8,
  obstacleFresnelIntensity: 1.5,
  obstacleOutlineColor: '#FFFFFF',
  obstacleOutlineEmission: 1.5,
  obstacleConfigs: { ...DEFAULT_OBSTACLE_CONFIGS },
  scrollSound: 'classic', scrollVolume: 1,
  placeSound: 'pop',      placeVolume: 1,
  deleteSound: 'shatter', deleteVolume: 1,
  noteSound: 'tone',
};

// ── Spark Particle Settings ──────────────────────────────────────────────────

export interface ParticleSettings {
  sparkCount: number;         // Number of particles
  sparkSpreadX: number;       // Horizontal spread
  sparkSpreadZ: number;       // Depth spread
  sparkSpeedMin: number;      // Minimum speed
  sparkSpeedMax: number;      // Maximum speed
  sparkYMin: number;          // Bottom respawn position
  sparkYMax: number;          // Top recycle position
  sparkTurbulence: number;    // Wobble amount
  sparkSize: number;          // Particle screen size
  sparkColor: string;         // Particle color 1 (hex)
  sparkColor2: string;        // Particle color 2 (hex)
  sparkColor3: string;        // Particle color 3 (hex)
  sparkColor4: string;        // Particle color 4 (hex)
  sparkColorBlend: boolean;   // Enable multi-color blending
  sparkColorTransition: number; // Seconds per color transition
  sparkFadeInSpeed: number;   // Fade-in rate
  sparkFadeOutSpeed: number;  // Fade-out rate
  // Position offset
  sparkOffsetX: number;       // Cloud center X offset
  sparkOffsetY: number;       // Cloud center Y offset
  sparkOffsetZ: number;       // Cloud center Z offset
  // Direction (angle in degrees: 0=up, 90=right, 180=down, 270=left)
  sparkDirectionAngle: number;  // Movement direction (degrees, 0=up)
  sparkDirectionTilt: number;   // Forward/back tilt (-90 to 90, 0=no tilt, positive=toward camera)
  // Rotation
  sparkRotationX: number;       // Cloud rotation speed around X axis (degrees/sec)
  sparkRotationY: number;       // Cloud rotation speed around Y axis (degrees/sec)
  // Loop — particles always visible (not just during automap)
  sparkLoop: boolean;           // When true, particles play continuously
}

export const DEFAULT_PARTICLES: ParticleSettings = {
  sparkCount: 300,
  sparkSpreadX: 8,
  sparkSpreadZ: 15,
  sparkSpeedMin: 2.0,
  sparkSpeedMax: 6.0,
  sparkYMin: -3,
  sparkYMax: 10,
  sparkTurbulence: 1.2,
  sparkSize: 5,
  sparkColor: '#E8B4F8',
  sparkColor2: '#5024FF',
  sparkColor3: '#00E5FF',
  sparkColor4: '#FF4081',
  sparkColorBlend: false,
  sparkColorTransition: 2.0,
  sparkFadeInSpeed: 5.0,
  sparkFadeOutSpeed: 1.5,
  sparkOffsetX: 0,
  sparkOffsetY: 0,
  sparkOffsetZ: 0,
  sparkDirectionAngle: 0,
  sparkDirectionTilt: 0,
  sparkRotationX: 0,
  sparkRotationY: 0,
  sparkLoop: false,
};

// ── Performance presets ──────────────────────────────────────────────────────
// Every knob each perf-pill preset (Potato / Perf / Auto / Quality) applies.
// These used to be hardcoded constants scattered across PerfPill, editorStore
// and perf/potato.ts; they are now per-preset USER SETTINGS with these defaults,
// edited from the Performance tab in either editor's Settings dialog (both
// editors read the same store, so a change applies live everywhere).

export type PerfMode = 'auto' | 'performance' | 'quality' | 'potato';

/** Flow-editor effect gates. The SR editor has no per-preset effects (its potato
 *  savings are aa/dpr/cap/horizon), so these only change the Flow editor. */
export interface PerfFlowFx {
  sparks: boolean;                 // hit burst particles
  waveformSprite: boolean;         // runway-edge waveform planes
  railSweep: boolean;              // animated trail sweep shader
  beatChipsAllBeats: boolean;      // false = number only measures (DOM sprites are pricey)
  minimapLiveDuringPlay: boolean;  // false = minimap freezes while playing
}

export interface PerfPresetConfig {
  /** Draw distance in beats for notes + obstacles. null = adaptive (the governor
   *  tunes it by framerate). */
  drawDistanceBeats: number | null;
  /** Render-resolution pin (fraction of native). null = the adaptive ladder owns
   *  it, trading sharpness against the fps target. */
  renderScale: number | null;
  /** Where the adaptive ladder STARTS when renderScale is null. null = don't
   *  seed — keep the ladder's own continuity (releasing a pin re-seeds from the
   *  rung on screen; a forced seed here would discard that). */
  ladderSeed: number | null;
  /** Target frame rate. 0 = uncapped. The tuner's thresholds follow the cap. */
  fpsCap: number;
  /** Hard ceiling on the device pixel ratio, applied before renderScale.
   *  null = no cap (the editor's own base dpr applies). */
  dprCap: number | null;
  /** false forces MSAA off (a context-creation flag — changing it remounts the
   *  canvas). true defers to the render-mode default (SR) / leaves it on (Flow). */
  antialias: boolean;
  flowFx: PerfFlowFx;
}

export type PerformanceSettings = Record<PerfMode, PerfPresetConfig>;

const ALL_FX_ON: PerfFlowFx = {
  sparks: true, waveformSprite: true, railSweep: true,
  beatChipsAllBeats: true, minimapLiveDuringPlay: true,
};

/** Defaults = the exact values each preset shipped with before they became
 *  editable. Potato's numbers are telemetry-derived (a user on Intel integrated
 *  graphics at 1920×1200 sat at 27fps avg / 15 p5): on that class of GPU the
 *  bottleneck is FILL RATE, so MSAA off + dpr 1 are the big levers, the 30fps
 *  cap hands half the GPU back (and a stable 30 reads better than 27-45
 *  jitter), and the ladder — not a pin — finds the sharpest rung the machine
 *  can hold that cap at, starting from 0.85. */
export const DEFAULT_PERFORMANCE: PerformanceSettings = {
  potato: {
    drawDistanceBeats: 12.5,
    renderScale: null,       // adaptive: the 30fps cap gives the ladder its target
    ladderSeed: 0.85,
    fpsCap: 30,
    dprCap: 1,
    antialias: false,
    flowFx: {
      sparks: false, waveformSprite: false, railSweep: false,
      beatChipsAllBeats: false, minimapLiveDuringPlay: false,
    },
  },
  performance: {
    drawDistanceBeats: 16,
    renderScale: 0.7,
    ladderSeed: null,
    fpsCap: 0,
    dprCap: null,
    antialias: true,
    flowFx: { ...ALL_FX_ON },
  },
  auto: {
    drawDistanceBeats: null, // adaptive horizon
    renderScale: null,       // adaptive ladder
    ladderSeed: null,
    fpsCap: 0,
    dprCap: null,
    antialias: true,
    flowFx: { ...ALL_FX_ON },
  },
  quality: {
    drawDistanceBeats: 50,
    renderScale: 1,
    ladderSeed: null,
    fpsCap: 0,
    dprCap: null,
    antialias: true,
    flowFx: { ...ALL_FX_ON },
  },
};

/** Build a NOTE_COLORS-compatible record from the current gameplay settings. */
export function getNoteColorsFromSettings(gp: GameplaySettings): Record<string, string> {
  return {
    left_hand: gp.noteColorLeft,
    right_hand: gp.noteColorRight,
    one_hand_special: gp.noteColorOneHand,
    both_hands_special: gp.noteColorBothHands,
  };
}

export interface InterfaceSettings {
  // Step lines (white lines on floor showing scroll step intervals)
  stepLineWidth: number;
  stepLineColor: string;
  stepLineOpacity: number;
  // Floor grid (beat/measure lines on the floor)
  gridLineWidth: number;
  gridMeasureLineWidth: number;
  gridLineColor: string;
  gridLineVisible: boolean;      // sub-beat lines (measure lines stay)
  /** Ease the snap lines out during playback (the old behaviour). OFF by
   *  default — reviewers want the grid while the music plays. */
  fadeBeatLinesOnPlay: boolean;
  beatGridVisible: boolean;      // master on/off for the whole 3D beat grid ("3D timeline")
  gridMeasureColor: string;
  gridCenterColor: string;
  gridCenterLineVisible: boolean;
  // Playhead / trigger area
  playheadFloorVisible: boolean;
  playheadLineWidth: number;
  playheadSidesVisible: boolean;
  playheadColor: string;
  playheadSideLineWidth: number;
  crosshairOpacity: number;
  // Tunnel edges
  edgeLineWidth: number;
  edgePrimaryColor: string;
  edgeSecondaryColor: string;
  // Background gradient
  bgGradientTop: string;
  bgGradientBottom: string;
  // Beat numbers — Flow-style chip: text (beatNumberColor) + container + outline.
  beatNumberColor: string;
  beatNumberSize: number;
  beatLabelBg: string;
  beatLabelOutline: string;
  // Floor plane
  floorColor: string;
  floorOpacity: number;
  // Grid Y offset (shift the entire floor grid up/down)
  gridYOffset: number;
  // 3D Lighting
  lightColor: string;
  lightIntensity: number;
  lightRotationX: number;  // degrees
  lightRotationY: number;  // degrees
  ambientColor: string;
  ambientIntensity: number;
  // Fog
  fogNear: number;
  fogFar: number;
  // Camera
  cameraFov: number;
  /** How fast the view TRAVELS to a new position when you jump/scrub while
   *  paused. 0 = teleport (classic), 1–10 = glide, higher is faster. */
  cameraTravelSpeed: number;
  // Point lights (3 accent lights in the tunnel)
  pointLight1Color: string;
  pointLight1Intensity: number;
  pointLight2Color: string;
  pointLight2Intensity: number;
  pointLight3Color: string;
  pointLight3Intensity: number;
  // Note visual tweaks
  noteEmissiveNormal: number;     // emissive intensity for regular notes
  noteEmissiveStemAura: number;   // emissive intensity when stem aura active
  noteEmissivePast: number;       // emissive intensity for notes past playhead
  noteOpacityPast: number;        // opacity for notes past playhead
  // Rail
  railOpacity: number;
  // UI Containers (sidebar section boxes)
  uiContainerBg: string;
  uiContainerBgOpacity: number;
  uiContainerOutline: string;
  uiContainerOutlineOpacity: number;
  uiContainerOutlineWidth: number;
  uiContainerMargin: number;
  uiContainerPadding: number;
  // UI Container gap (space between rows/columns inside containers)
  uiContainerGap: number;
  // Note buttons (Notes, Obstacles, etc.)
  toolBtnBgOpacity: number;
  toolBtnOutlineOpacity: number;
  toolBtnOutlineWidth: number;
  // Slider appearance
  sliderGradientFrom: string;
  sliderGradientTo: string;
  sliderTrackHeight: number;
  sliderThumbSize: number;
  sliderThumbColor: string;
  sliderTrackBg: string;
  sliderTrackBorderColor: string;
  // Typography — Section Titles (e.g. "Body Proportions", "Sidebar Containers")
  typoTitleSize: number;
  typoTitleColor: string;
  typoTitleMarginBottom: number;
  // Typography — Sub-section labels (e.g. "Default Position", "Rotation")
  typoSubtitleSize: number;
  typoSubtitleColor: string;
  typoSubtitleMarginTop: number;
  typoSubtitleMarginBottom: number;
  // Typography — Body / labels (slider labels, color labels, hint text)
  typoBodySize: number;
  typoBodyColor: string;
  // Typography — Value / secondary text (formatted slider values, hex codes)
  typoValueSize: number;
  typoValueColor: string;
  // App Logo
  logoSize: number;  // 0 = hidden, otherwise height in px
  /** Synth VR only: which edge of the editing area the waveform strip sits on. 'bottom' is the strip
   *  above the transport pills, as it has always been. Left and right run bottom-to-top (time 0 at the
   *  bottom of the screen). */
  waveformEdge: WaveformEdge;
}

export type WaveformEdge = 'left' | 'right' | 'bottom' | 'top';
export const WAVEFORM_EDGES: readonly WaveformEdge[] = ['left', 'right', 'bottom', 'top'];

export const DEFAULT_INTERFACE: InterfaceSettings = {
  stepLineWidth: 0.8,
  stepLineColor: '#ffffff',
  stepLineOpacity: 0.25,
  gridLineWidth: 0.5,
  gridMeasureLineWidth: 1.5,
  gridLineColor: '#1A0F3D',
  gridLineVisible: true,
  fadeBeatLinesOnPlay: false,
  beatGridVisible: true,
  gridMeasureColor: '#3B1A8B',
  gridCenterColor: '#5B2EC5',
  gridCenterLineVisible: true,
  playheadFloorVisible: true,
  playheadLineWidth: 3,
  playheadSidesVisible: true,
  playheadColor: color.accent,
  playheadSideLineWidth: 1.5,
  crosshairOpacity: 0.5,
  edgeLineWidth: 2,
  edgePrimaryColor: '#7C3AED',
  edgeSecondaryColor: '#3B1A8B',
  bgGradientTop: '#0a0520',
  bgGradientBottom: '#050510',
  beatNumberColor: '#f4f0ff',
  beatNumberSize: 0.2,
  beatLabelBg: '#0a0520',
  beatLabelOutline: '#d946ef',
  floorColor: '#030308',
  floorOpacity: 0.9,
  gridYOffset: 0,
  lightColor: color.accent,
  lightIntensity: 0.3,
  lightRotationX: 60,
  lightRotationY: 0,
  ambientColor: '#1a1030',
  ambientIntensity: 0.2,
  fogNear: 5,
  fogFar: 120,
  // 35° = the desktop (Unity) editor's default camera. Reviewers asked for the
  // same framing they map with there; the old 70 exaggerated the perspective.
  cameraFov: 35,
  cameraTravelSpeed: 6,
  pointLight1Color: '#7C3AED',
  pointLight1Intensity: 0.6,
  pointLight2Color: color.accent,
  pointLight2Intensity: 0.3,
  pointLight3Color: color.accent,
  pointLight3Intensity: 0.2,
  noteEmissiveNormal: 0.5,
  noteEmissiveStemAura: 0.55,
  noteEmissivePast: 0.2,
  noteOpacityPast: 0.5,
  railOpacity: 0.8,
  uiContainerBg: '#0f0f1e',
  uiContainerBgOpacity: 0.6,
  uiContainerOutline: color.accent,
  uiContainerOutlineOpacity: 0.35,
  uiContainerOutlineWidth: 1.5,
  uiContainerMargin: 8,
  uiContainerPadding: 10,
  uiContainerGap: 4,
  toolBtnBgOpacity: 0.8,
  toolBtnOutlineOpacity: 1.0,
  toolBtnOutlineWidth: 2,
  sliderGradientFrom: '#7C3AED',
  sliderGradientTo: '#D946EF',
  sliderTrackHeight: 8,
  sliderThumbSize: 14,
  sliderThumbColor: '#ffffff',
  // Track fill + outline match the sidebar buttons (e.g. Camera View) so the
  // unfilled slider reads as the same surface, with a visible outline.
  sliderTrackBg: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  sliderTrackBorderColor: 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
  // Typography defaults
  typoTitleSize: 10,
  typoTitleColor: color.accent,
  typoTitleMarginBottom: 6,
  typoSubtitleSize: 9,
  typoSubtitleColor: color.inkDim,
  typoSubtitleMarginTop: 8,
  typoSubtitleMarginBottom: 4,
  typoBodySize: 10,
  typoBodyColor: color.ink,
  typoValueSize: 9,
  typoValueColor: color.ink,
  logoSize: 40,
  waveformEdge: 'bottom',
};

// Migration: maps that saved the old (purple-tinted) default body/value text
// colours adopt the new white defaults. Customised colours are left untouched.
export function migrateTypographyColors(iface: InterfaceSettings): InterfaceSettings {
  const next = { ...iface };
  if (next.typoBodyColor === color.inkDim) next.typoBodyColor = color.ink;
  if (next.typoValueColor === color.inkFaint) next.typoValueColor = color.ink;
  return next;
}

/**
 * The Select tool (toolMode 'select'): its buttons, its V key, and the click-to-select it gave notes,
 * walls and decor groups. Off while the area selection (Z) is the way to select. The tool and the
 * code that reads this stay in place so it can be switched back on, or reused, later.
 */
export const SELECT_TOOL_ENABLED = false;

/**
 * The Synth Riders VR editor view. Every selection-and-editing behaviour in the SRVR "selection
 * improvements" (docs/spec/srvr/ SRVR-0325 to SRVR-0328 and the changes they sit beside) belongs to this
 * view alone: the scene, the key engine and the transport are shared with the Console editor, which is
 * not ours to change, so each of them asks this before taking the new path and leaves Console on the
 * old one.
 */
export const isSynthVrView = (view: string | undefined): boolean => view === 'synth-rider';

/** Whether the camera is in mouse-driven free-look. Free everywhere; Custom 1-3 (cam1..cam3) only in
 *  Synth VR, so a saved cam view can never put Console into free-look. */
export const isFreeLookCamera = (cameraView: string | undefined, activeView: string | undefined): boolean =>
  cameraView === 'free' || (isSynthVrView(activeView) && (cameraView === 'cam1' || cameraView === 'cam2' || cameraView === 'cam3'));

/** Whether the Select tool exists in `view`: gone in Synth VR while SELECT_TOOL_ENABLED is off, and
 *  untouched everywhere else. */
export const selectToolAvailable = (view: string | undefined): boolean => !isSynthVrView(view) || SELECT_TOOL_ENABLED;
